import {useAccessSubscription} from "../../../firebase/realtime_database/useAccessSubscription";
import {useSession} from "../../../firebase/UserProvider";
import {useReportStore} from "../useReportStore";

function hasSessionRole(sessionRole, sessionRoles, target) {
    return sessionRole === target
        || (Array.isArray(sessionRoles) && sessionRoles.includes(target));
}

function privilegedReadRole(sessionRole, sessionRoles, reportStatus) {
    if (hasSessionRole(sessionRole, sessionRoles, 'admin')) {
        return 'viewer';
    }
    if (hasSessionRole(sessionRole, sessionRoles, 'collection_manager')) {
        return 'viewer';
    }
    if (hasSessionRole(sessionRole, sessionRoles, 'reviewer')) {
        const normalized = (reportStatus || '').toLowerCase();
        if (reportStatus === 'Awaiting Review' || normalized === 'approved') {
            return 'viewer';
        }
    }
    return null;
}

/**
 * Custom hook that manages report access control.
 *
 * @param {string} reportId - The ID of the report
 * @param {Object} user - The current user object
 * @returns {Object} - { role, isViewer, allowedRole, accessLoading, accessError }
 */
export const useReportAccess = (reportId, user) => {
    const {role: sessionRole, roles: sessionRoles} = useSession() || {};
    const reportOwner = useReportStore(s => s.owner);
    const reportShared = useReportStore(s => s.shared);
    const reportStatus = useReportStore(s => s.status);
    const loadedReportId = useReportStore(s => s.id);

    const {
        value: accessValue,
        isLoading: accessLoading,
        error: accessError
    } = useAccessSubscription(reportId, user?.uid);

    let role = typeof accessValue === 'string' ? accessValue : null;
    if (!role && user?.uid && loadedReportId === reportId) {
        if (reportOwner === user.uid) {
            role = 'owner';
        } else if (Array.isArray(reportShared) && reportShared.includes(user.uid)) {
            role = 'viewer';
        } else {
            role = privilegedReadRole(sessionRole, sessionRoles, reportStatus);
        }
    }

    const allowedRole = role === 'owner' || role === 'editor' || role === 'viewer';
    const isViewer = role === 'viewer';

    return {role, isViewer, allowedRole, accessLoading, accessError};
};
